import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../../../layouts/Default";
import { Main } from "../../../../themes/Default/components/Main";
import { Title } from "../../../../themes/Default/components/Title";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Button, ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { List } from "../../../../themes/Default/components/List";
import { Header as ListHeader } from "../../../../themes/Default/components/List/components/Header";
import { Body as ListBody } from "../../../../themes/Default/components/List/components/Body";
import { TableHead } from "../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableBody } from "../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableRow } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableDataCell } from "../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Title as ListTitle } from "../../../../themes/Default/components/List/components/Header/components/Title";
import { Checkbox } from "../../../../themes/Default/components/Form/components/Checkbox";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { useTranslation } from "react-i18next";
import { employeesManagement } from "../../../../hooks/api/EmployeesManagement";
import { Color } from "../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell/components/Color";
import { Slide, toast, ToastContainer } from "react-toastify";
import { Toaster } from "../../../../themes/Default/components/Toaster";

const Notes = () => {
    const { t } = useTranslation("common");
    const { employeeId } = useParams();
    const navigate = useNavigate();
    const [disableDelete, setDisableDelete] = useState(true);
    const [selectedNotes, setSelectedNotes] = useState([]);
    const [notes, setNotes] = useState();
    const [employee, setEmployee] = useState();
    const { useNotesListMutation, useViewEmployeeMutation, useDeleteNoteMutation } = employeesManagement;
    const [notesList, { isLoading }] = useNotesListMutation();
    const [deleteNote] = useDeleteNoteMutation();
    const [viewEmployee] = useViewEmployeeMutation();

    const tabs = [
        { title: t("details"), link: "/settings/reports", onClick: () => gotoDetailsTab(employeeId) },
        { title: t("documents"), link: "/settings/reports", onClick: () => gotoDocumentsTab(employeeId) },
        { title: t("notes"), link: "/settings/reports", isActive: true },
    ];

    const gotoDetailsTab = (employeeId) => {
        navigate("/employees/edit/" + employeeId);
    };

    const gotoDocumentsTab = (employeeId) => {
        navigate("/employees/" + employeeId + "/documents");
    };

    const gotoNew = () => {
        navigate(`/employees/${employeeId}/notes/add`);
    };

    const onSelectionChange = (id) => {
        setSelectedNotes((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        for (let i = 0; i < selectedNotes?.length; i++) {
                            const noteId = selectedNotes[i];
                            deleteNote({ employeeId, noteId }).then((result) => {
                                notesList(employeeId).then((result) => {
                                    const notes = result?.data?.records;
                                    setNotes(notes);
                                });
                                setDisableDelete(true);
                                toast.info(result?.data?.info?.responseMessage);
                            });
                        }
                    }}
                />
            ),
            {
                closeButton: false,
                autoClose: false,
                progress: false,
            },
        );
    };

    useEffect(() => {
        if (selectedNotes.length > 0) {
            setDisableDelete(false);
        } else {
            setDisableDelete(true);
        }
    }, [selectedNotes]);

    useEffect(() => {
        notesList(employeeId).then((result) => {
            const notes = result?.data?.records;

            setNotes(notes);
        });
    }, [notesList]);

    useEffect(() => {
        viewEmployee(employeeId).then((result) => {
            setEmployee(result?.data);
        });
    }, [viewEmployee, employeeId]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/employees" />
                <Button title="Add note" icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                <Button title={t("delete")} icon="fa-regular fa-trash-can" isDisabled={disableDelete} onClick={onDelete}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>
                    {employee?.firstName} {employee?.lastName}
                </Title>
                <Breadcrumps>
                    <Link title={t("employees.title")} icon="fa-user-tie" />
                    <Link title={t("notes")} />
                    <Link title={t("settings.list")} isActive={true} />
                </Breadcrumps>
                {!isLoading && (
                    <List>
                        <ListHeader>
                            <ListTitle title={t("notes")} subtitle="List of notes" />
                        </ListHeader>
                        <ListBody>
                            <TableHead>
                                <TableRow>
                                    <TableHeadCell></TableHeadCell>
                                    <TableHeadCell>
                                        <span>Color</span>
                                    </TableHeadCell>
                                    <TableHeadCell>
                                        <span>Type Name</span>
                                    </TableHeadCell>
                                    <TableHeadCell>
                                        <span>Title</span>
                                    </TableHeadCell>
                                    <TableHeadCell>
                                        <span>Description</span>
                                    </TableHeadCell>
                                    <TableHeadCell></TableHeadCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {notes?.length > 0 &&
                                    notes?.map((note, index) => {
                                        return (
                                            <TableRow key={note.id}>
                                                <TableDataCell>
                                                    <Checkbox name="isBlockClass" value={note.id} onChange={() => onSelectionChange(note.id)} />
                                                </TableDataCell>
                                                <TableDataCell>
                                                    <Color color={note?.typeColor} />
                                                </TableDataCell>
                                                <TableDataCell>{note.typeName}</TableDataCell>
                                                <TableDataCell>{note.title}</TableDataCell>
                                                <TableDataCell>{note.description}</TableDataCell>
                                                <TableDataCell></TableDataCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </ListBody>
                    </List>
                )}
            </Main>
        </DefaultLayout>
    );
};

export default Notes;
